import React, { useState, useEffect } from 'react';
import { 
  Settings, 
  Plus, 
  Edit, 
  Trash2, 
  Save, 
  X, 
  ChevronDown, 
  ChevronRight,
  Database,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  GripVertical,
  Eye,
  EyeOff
} from 'lucide-react';
import { useToast, ToastContainer } from '../common/Toast';
import ConfirmationModal from '../common/ConfirmationModal';
import MasterDataService from '../../services/masterDataService';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

const MasterDataManagement = () => {
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [masterDataItems, setMasterDataItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [draggedItem, setDraggedItem] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showCreateCategoryModal, setShowCreateCategoryModal] = useState(false);
  const [newCategoryData, setNewCategoryData] = useState({
    category: '',
    code: '',
    label: '',
    description: '',
    isActive: true
  });
  const [categoryErrors, setCategoryErrors] = useState({});
  const { toasts, success, error: showError, warning, info, removeToast } = useToast();

  // Form state
  const [formData, setFormData] = useState({
    category: '',
    code: '',
    label: '',
    description: '',
    isActive: true,
    sortOrder: 0,
    notes: '',
    parentCategory: '',
    parentValue: '',
    metadata: {}
  });

  const [errors, setErrors] = useState({});

  // Load categories
  const loadCategories = async () => {
    try {
      setLoading(true);
      const response = await MasterDataService.getCategories();
      if (response.success) {
        setCategories(response.data);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
      showError('Failed to load master data categories');
    } finally {
      setLoading(false);
    }
  };

  // Load master data items for selected category
  const loadMasterDataItems = async (category, parentValue = null) => {
    try {
      //console.log('Loading master data items for category:', category);
      const response = await MasterDataService.getByCategory(category, parentValue);
      //console.log('Master data response:', response);
      if (response.success) {
        setMasterDataItems(response.data);
        //console.log('Set master data items:', response.data);
      } else {
        console.error('API response not successful:', response);
        showError('Failed to load master data items');
      }
    } catch (error) {
      console.error('Error loading master data items:', error);
      showError('Failed to load master data items');
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    if (selectedCategory) {
      loadMasterDataItems(selectedCategory.category);
    }
  }, [selectedCategory]);

  // Calculate next sort order for a category
  const getNextSortOrder = (category) => {
    if (!category || !masterDataItems.length) {
      //console.log('No items found, returning sort order 1');
      return 1;
    }
    
    const maxSortOrder = Math.max(...masterDataItems.map(item => item.sortOrder || 0));
    const nextSortOrder = maxSortOrder + 1;
    //console.log(`Current max sort order: ${maxSortOrder}, next sort order: ${nextSortOrder}`);
    return nextSortOrder;
  };

  // Handle category selection
  const handleCategorySelect = (category) => {
    //console.log('Category selected:', category);
    setSelectedCategory(category);
    setShowForm(false);
    setEditingItem(null);
    
    // Calculate next sort order for this category
    const nextSortOrder = getNextSortOrder(category);
    
    setFormData({
      category: category.category,
      code: '',
      label: '',
      description: '',
      isActive: true,
      sortOrder: nextSortOrder,
      notes: '',
      parentCategory: '',
      parentValue: '',
      metadata: {}
    });
  };

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  // Validate category name format
  const validateCategoryName = (category) => {
    if (!category || !category.trim()) {
      return 'Category name is required';
    }
    const trimmed = category.trim();
    if (!/^[A-Z][a-zA-Z0-9]*$/.test(trimmed)) {
      return 'Category must start with an uppercase letter and contain only letters and numbers (e.g., ContractType, CustomCategory)';
    }
    return null;
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.code.trim()) {
      newErrors.code = 'Code is required';
    }

    if (!formData.label.trim()) {
      newErrors.label = 'Label is required';
    }

    if (MasterDataService.isDependentCategory(formData.category)) {
      if (!formData.parentValue) {
        newErrors.parentValue = 'Parent value is required for dependent categories';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Validate category creation form
  const validateCategoryForm = () => {
    const newErrors = {};

    const categoryError = validateCategoryName(newCategoryData.category);
    if (categoryError) {
      newErrors.category = categoryError;
    }

    if (!newCategoryData.code.trim()) {
      newErrors.code = 'Code is required';
    }

    if (!newCategoryData.label.trim()) {
      newErrors.label = 'Label is required';
    }

    setCategoryErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle create category
  const handleCreateCategory = async () => {
    if (!validateCategoryForm()) {
      showError('Please fix validation errors');
      return;
    }

    try {
      const categoryData = {
        category: newCategoryData.category.trim(),
        code: newCategoryData.code.trim(),
        label: newCategoryData.label.trim(),
        description: newCategoryData.description?.trim() || undefined,
        isActive: newCategoryData.isActive
      };

      await MasterDataService.create(categoryData);
      success(`Category "${newCategoryData.category}" created successfully`);
      
      setShowCreateCategoryModal(false);
      setNewCategoryData({
        category: '',
        code: '',
        label: '',
        description: '',
        isActive: true
      });
      setCategoryErrors({});
      
      // Reload categories and select the new one
      await loadCategories();
      const updatedCategories = await MasterDataService.getCategories();
      if (updatedCategories.success) {
        const newCategory = updatedCategories.data.find(c => c.category === categoryData.category);
        if (newCategory) {
          handleCategorySelect(newCategory);
        }
      }
    } catch (error) {
      console.error('Error creating category:', error);
      if (error.type === 'validation') {
        const errorObj = {};
        if (error.errors) {
          error.errors.forEach(err => {
            errorObj[err.field || err.path] = err.message;
          });
        }
        setCategoryErrors(errorObj);
        showError(error.message || 'Please fix validation errors');
      } else {
        showError(error.message || 'Failed to create category');
      }
    }
  };

  // Handle save
  const handleSave = async () => {
    if (!validateForm()) {
      showError('Please fix validation errors');
      return;
    }

    // Clean up form data - remove empty strings for optional fields
    const cleanedFormData = {
      ...formData,
      parentCategory: formData.parentCategory || undefined,
      parentValue: formData.parentValue || undefined,
      description: formData.description || undefined,
      notes: formData.notes || undefined
    };

    //console.log('Original form data:', formData);
    //console.log('Cleaned form data:', cleanedFormData);

    try {
      if (editingItem) {
        await MasterDataService.update(editingItem.id, cleanedFormData);
        success('Master data item updated successfully');
      } else {
        await MasterDataService.create(cleanedFormData);
        success('Master data item created successfully');
      }

      setShowForm(false);
      setEditingItem(null);
      
      // Reset form with updated sort order for next item
      if (selectedCategory) {
        const nextSortOrder = getNextSortOrder(selectedCategory);
        setFormData(prev => ({
          ...prev,
          code: '',
          label: '',
          description: '',
          notes: '',
          sortOrder: nextSortOrder
        }));
      }
      
      loadMasterDataItems(selectedCategory.category);
      loadCategories(); // Refresh category counts
    } catch (error) {
      console.error('Error saving master data item:', error);
      if (error.type === 'validation') {
        const errorObj = {};
        error.errors.forEach(err => {
          errorObj[err.field] = err.message;
        });
        setErrors(errorObj);
        showError('Please fix validation errors');
      } else {
        showError(error.message || 'Failed to save master data item');
      }
    }
  };

  // Handle edit
  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      category: item.category,
      code: item.code,
      label: item.label,
      description: item.description || '',
      isActive: item.isActive,
      sortOrder: item.sortOrder,
      notes: item.notes || '',
      parentCategory: item.parentCategory || '',
      parentValue: item.parentValue || '',
      metadata: item.metadata || {}
    });
    setShowForm(true);
  };

  // Handle delete
  const handleDelete = (item) => {
    setItemToDelete(item);
    setShowDeleteModal(true);
  };

  // Confirm delete
  const confirmDelete = async () => {
    if (!itemToDelete) return;
    
    try {
      await MasterDataService.delete(itemToDelete.id);
      success('Master data item deleted successfully');
      loadMasterDataItems(selectedCategory.category);
      loadCategories(); // Refresh category counts
    } catch (error) {
      console.error('Error deleting master data item:', error);
      showError(error.message || 'Failed to delete master data item');
    } finally {
      setShowDeleteModal(false);
      setItemToDelete(null);
    }
  };

  // Handle toggle active status
  const handleToggleActive = async (item) => {
    try {
      await MasterDataService.update(item.id, { isActive: !item.isActive });
      success(`Item ${item.isActive ? 'deactivated' : 'activated'} successfully`);
      loadMasterDataItems(selectedCategory.category);
      loadCategories(); // Refresh category counts
    } catch (error) {
      console.error('Error toggling active status:', error);
      showError('Failed to update item status');
    }
  };

  // Handle cancel
  const handleCancel = () => {
    setShowForm(false);
    setEditingItem(null);
    setFormData({
      category: selectedCategory?.category || '',
      code: '',
      label: '',
      description: '',
      isActive: true,
      sortOrder: 0,
      notes: '',
      parentCategory: '',
      parentValue: '',
      metadata: {}
    });
    setErrors({});
  };

  // Handle drag start
  const handleDragStart = (e, item) => {
    setDraggedItem(item);
  };

  // Handle drag over
  const handleDragOver = (e) => {
    e.preventDefault();
  };

  // Handle drop
  const handleDrop = async (e, targetItem) => {
    e.preventDefault();
    
    if (!draggedItem || draggedItem.id === targetItem.id) {
      setDraggedItem(null);
      return;
    }

    try {
      // Create new order array
      const newOrder = [...masterDataItems];
      const draggedIndex = newOrder.findIndex(item => item.id === draggedItem.id);
      const targetIndex = newOrder.findIndex(item => item.id === targetItem.id);
      
      // Remove dragged item and insert at new position
      const [removed] = newOrder.splice(draggedIndex, 1);
      newOrder.splice(targetIndex, 0, removed);
      
      // Update sort orders
      const reorderData = newOrder.map((item, index) => ({
        id: item.id,
        sortOrder: index + 1
      }));
      
      await MasterDataService.reorder(selectedCategory.category, reorderData);
      success('Items reordered successfully');
      loadMasterDataItems(selectedCategory.category);
    } catch (error) {
      console.error('Error reordering items:', error);
      showError('Failed to reorder items');
    } finally {
      setDraggedItem(null);
    }
  };

  // Toggle category expansion
  const toggleCategoryExpansion = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // Render form field
  const renderField = (label, field, type = 'text', options = null) => {
    const hasError = errors[field];
    
    return (
      <div style={{ marginBottom: '16px' }}>
        <label style={{
          display: 'block',
          fontSize: '14px',
          fontWeight: '500',
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
          color: 'var(--text-primary)',
          marginBottom: '6px'
        }}>
          {label}
          {hasError && <span style={{ color: 'var(--error-color, #dc3545)', marginLeft: '4px' }}>*</span>}
        </label>
        
        {type === 'select' ? (
          <select
            value={formData[field]}
            onChange={(e) => handleInputChange(field, e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: `1px solid ${hasError ? 'var(--error-color, #dc3545)' : 'var(--border-color, #e5e7eb)'}`,
              borderRadius: '6px',
              fontSize: '14px',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
              outline: 'none'
            }}
          >
            <option value="">Select {label}</option>
            {options?.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ) : type === 'textarea' ? (
          <textarea
            value={formData[field]}
            onChange={(e) => handleInputChange(field, e.target.value)}
            rows={3}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: `1px solid ${hasError ? 'var(--error-color, #dc3545)' : 'var(--border-color, #e5e7eb)'}`,
              borderRadius: '6px',
              fontSize: '14px',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
              outline: 'none',
              resize: 'vertical'
            }}
          />
        ) : type === 'checkbox' ? (
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif' }}>
            <input
              type="checkbox"
              checked={formData[field]}
              onChange={(e) => handleInputChange(field, e.target.checked)}
            />
            <span style={{ fontSize: '14px',               color: 'var(--text-primary)', fontFamily: 'var(--font-family)' }}>
              {label}
            </span>
          </label>
        ) : (
          <input
            type={type}
            value={formData[field]}
            onChange={(e) => handleInputChange(field, e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: `1px solid ${hasError ? 'var(--error-color, #dc3545)' : 'var(--border-color, #e5e7eb)'}`,
              borderRadius: '6px',
              fontSize: '14px',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
              outline: 'none'
            }}
          />
        )}
        
        {hasError && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            marginTop: '4px',
            fontSize: '12px',
            color: '#dc3545'
          }}>
            <AlertCircle size={12} />
            {hasError}
          </div>
        )}
      </div>
    );
  };

  // Define header actions
  const headerActions = [
    {
      label: '',
      icon: <RefreshCw size={18} className={loading ? 'spinning' : ''} />,
      onClick: loadCategories,
      variant: 'outline',
      tooltip: 'Refresh categories',
      disabled: loading
    },
    {
      label: showForm ? 'Cancel' : 'Add Item',
      icon: showForm ? <X size={18} /> : <Plus size={18} />,
      onClick: () => {
        if (!showForm && selectedCategory) {
          // When opening the form for a new item, update the sort order
          const nextSortOrder = getNextSortOrder(selectedCategory);
          setFormData(prev => ({
            ...prev,
            sortOrder: nextSortOrder
          }));
        }
        setShowForm(!showForm);
      },
      variant: 'primary',
      tooltip: showForm ? 'Cancel adding item' : 'Add new master data item'
    }
  ];

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader 
        title="Master Data Management"
        description="Manage dropdown values and configuration data"
        actions={headerActions}
      />
      <div className="page-container">

      <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: '24px' }}>
        {/* Categories Sidebar */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e5e7eb',
          padding: '20px',
          height: 'fit-content'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: 'var(--text-primary)',
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Database size={20} style={{ color: 'var(--primary-color)' }} />
              Categories
            </h3>
            <button
              onClick={() => setShowCreateCategoryModal(true)}
              style={{
                padding: '6px 10px',
                backgroundColor: 'var(--primary-color)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '500',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#3a6ba5'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--primary-color)'}
              title="Create new category"
            >
              <Plus size={14} />
              New
            </button>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <RefreshCw size={20} className="animate-spin" />
              <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginTop: '8px' }}>Loading...</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {categories.map(category => (
                <div key={category.category}>
                  <button
                    onClick={() => handleCategorySelect(category)}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '12px',
                      backgroundColor: selectedCategory?.category === category.category ? 'rgba(70, 120, 190, 0.1)' : 'transparent',
                      color: selectedCategory?.category === category.category ? 'var(--primary-color)' : 'var(--text-primary)',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                      textAlign: 'left',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (selectedCategory?.category !== category.category) {
                        e.target.style.backgroundColor = '#f9fafb';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedCategory?.category !== category.category) {
                        e.target.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '500' }}>
                        {MasterDataService.getCategoryDisplayName(category.category)}
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                        {category.active} active • {category.total} total
                      </div>
                    </div>
                    {category.active > 0 && (
                      <CheckCircle size={16} color="#28a745" />
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Main Content */}
        <div>
          {selectedCategory ? (
            <div>
              {/* Category Header */}
              <div style={{
                backgroundColor: 'white',
                padding: '20px',
                borderRadius: '12px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                border: '1px solid #e5e7eb',
                marginBottom: '20px'
              }}>
                <h2 style={{
                  fontSize: '20px',
                  fontWeight: '600',
                  color: 'var(--text-primary)',
                  margin: '0 0 8px 0'
                }}>
                  {MasterDataService.getCategoryDisplayName(selectedCategory.category)}
                </h2>
                <p style={{
                  fontSize: '14px',
                  color: 'var(--text-secondary)',
                  margin: 0
                }}>
                  {MasterDataService.getCategoryDescription(selectedCategory.category)}
                </p>
              </div>

              {/* Form */}
              {showForm && (
                <div style={{
                  backgroundColor: 'white',
                  padding: '24px',
                  borderRadius: '12px',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                  border: '1px solid #e5e7eb',
                  marginBottom: '20px'
                }}>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: 'var(--text-primary)',
                    margin: '0 0 20px 0'
                  }}>
                    {editingItem ? 'Edit Item' : 'Add New Item'}
                  </h3>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                    <div>
                      {renderField('Code', 'code', 'text')}
                      {renderField('Label', 'label', 'text')}
                      {renderField('Description', 'description', 'textarea')}
                    </div>
                    <div>
                      {renderField('Sort Order', 'sortOrder', 'number')}
                      {renderField('Notes', 'notes', 'textarea')}
                      {renderField('Active', 'isActive', 'checkbox')}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                    <button
                      onClick={handleSave}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '10px 16px',
                        backgroundColor: 'var(--primary-color)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                      }}
                    >
                      <Save size={16} />
                      {editingItem ? 'Update' : 'Create'}
                    </button>
                    <button
                      onClick={handleCancel}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '10px 16px',
                        backgroundColor: 'transparent',
                        color: 'var(--text-secondary)',
                        border: '1px solid var(--border-color, #e5e7eb)',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                      }}
                    >
                      <X size={16} />
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Items List */}
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                border: '1px solid #e5e7eb',
                overflow: 'hidden'
              }}>
                <div style={{
                  padding: '20px',
                  borderBottom: '1px solid #e5e7eb',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: 'var(--text-primary)',
                    margin: 0
                  }}>
                    Items ({masterDataItems.length})
                  </h3>
                </div>

                <div>
                  {masterDataItems.length === 0 ? (
                    <div style={{
                      padding: '40px',
                      textAlign: 'center',
                      color: 'var(--text-secondary)'
                    }}>
                      <Database size={48} style={{ margin: '0 auto 16px', opacity: 0.5 }} />
                      <p style={{ fontSize: '16px', margin: '0 0 8px 0' }}>No items found</p>
                      <p style={{ fontSize: '14px', margin: 0 }}>Click "Add Item" to create the first item</p>
                    </div>
                  ) : (
                    masterDataItems.map((item, index) => (
                      <div
                        key={item.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, item)}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, item)}
                        style={{
                          padding: '16px 20px',
                          borderBottom: index < masterDataItems.length - 1 ? '1px solid #f3f4f6' : 'none',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          cursor: 'move',
                          transition: 'background-color 0.2s ease'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                      >
                        <GripVertical size={16} color="#9ca3af" />
                        
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                            <span style={{ fontSize: '14px', fontWeight: '500', color: 'var(--text-primary)' }}>
                              {item.label}
                            </span>
                            <span style={{
                              padding: '2px 6px',
                              fontSize: '11px',
                              fontWeight: '500',
                              backgroundColor: 'var(--border-color, #e5e7eb)',
                              color: 'var(--text-secondary)',
                              borderRadius: '4px'
                            }}>
                              {item.code}
                            </span>
                            {item.isActive ? (
                              <CheckCircle size={14} color="#28a745" />
                            ) : (
                              <EyeOff size={14} style={{ color: 'var(--text-secondary)' }} />
                            )}
                          </div>
                          {item.description && (
                            <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: 0 }}>
                              {item.description}
                            </p>
                          )}
                        </div>

                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => handleToggleActive(item)}
                            style={{
                              padding: '6px',
                              backgroundColor: 'transparent',
                              color: item.isActive ? '#dc3545' : '#28a745',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                            title={item.isActive ? 'Deactivate' : 'Activate'}
                          >
                            {item.isActive ? <EyeOff size={14} /> : <Eye size={14} />}
                          </button>
                          <button
                            onClick={() => handleEdit(item)}
                            style={{
                              padding: '6px',
                              backgroundColor: 'transparent',
                              color: 'var(--text-secondary)',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                            title="Edit"
                          >
                            <Edit size={14} />
                          </button>
                          <button
                            onClick={() => handleDelete(item)}
                            style={{
                              padding: '6px',
                              backgroundColor: 'transparent',
                              color: '#dc3545',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                            title="Delete"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div style={{
              backgroundColor: 'white',
              padding: '40px',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb',
              textAlign: 'center'
            }}>
              <Settings size={48} style={{ margin: '0 auto 16px', opacity: 0.5, color: 'var(--text-secondary)' }} />
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)', margin: '0 0 8px 0' }}>
                Select a Category
              </h3>
              <p style={{ fontSize: '14px', color: 'var(--text-secondary)', margin: 0 }}>
                Choose a category from the sidebar to manage its master data items
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* Toast Container */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      
      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setItemToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Master Data Item"
        message={`Are you sure you want to delete "${itemToDelete?.label}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      {/* Create Category Modal */}
      {showCreateCategoryModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}
        onClick={() => {
          setShowCreateCategoryModal(false);
          setNewCategoryData({
            category: '',
            code: '',
            label: '',
            description: '',
            isActive: true
          });
          setCategoryErrors({});
        }}
        >
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)'
          }}
          onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{
                fontSize: '20px',
                fontWeight: '600',
                color: 'var(--text-primary)',
                margin: 0,
                fontFamily: 'var(--font-family)'
              }}>
                Create New Category
              </h2>
              <button
                onClick={() => {
                  setShowCreateCategoryModal(false);
                  setNewCategoryData({
                    category: '',
                    code: '',
                    label: '',
                    description: '',
                    isActive: true
                  });
                  setCategoryErrors({});
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <X size={20} style={{ color: 'var(--text-secondary)' }} />
              </button>
            </div>

            <p style={{
              fontSize: '14px',
              color: 'var(--text-secondary)',
              margin: '0 0 20px 0',
              fontFamily: 'var(--font-family)'
            }}>
              Create a new category by adding the first item. The category name must start with an uppercase letter and contain only letters and numbers (e.g., CustomCategory, MyCategory).
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'var(--text-primary)',
                  marginBottom: '6px',
                  fontFamily: 'var(--font-family)'
                }}>
                  Category Name *
                </label>
                <input
                  type="text"
                  value={newCategoryData.category}
                  onChange={(e) => {
                    const value = e.target.value;
                    setNewCategoryData(prev => ({ ...prev, category: value }));
                    if (categoryErrors.category) {
                      setCategoryErrors(prev => ({ ...prev, category: null }));
                    }
                  }}
                  placeholder="e.g., CustomCategory, MyCategory"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: `1px solid ${categoryErrors.category ? '#dc3545' : '#e5e7eb'}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'var(--font-family)',
                    outline: 'none'
                  }}
                />
                {categoryErrors.category && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    marginTop: '4px',
                    fontSize: '12px',
                    color: '#dc3545'
                  }}>
                    <AlertCircle size={12} />
                    {categoryErrors.category}
                  </div>
                )}
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'var(--text-primary)',
                  marginBottom: '6px',
                  fontFamily: 'var(--font-family)'
                }}>
                  Code *
                </label>
                <input
                  type="text"
                  value={newCategoryData.code}
                  onChange={(e) => {
                    setNewCategoryData(prev => ({ ...prev, code: e.target.value }));
                    if (categoryErrors.code) {
                      setCategoryErrors(prev => ({ ...prev, code: null }));
                    }
                  }}
                  placeholder="Unique code for this item"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: `1px solid ${categoryErrors.code ? '#dc3545' : '#e5e7eb'}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'var(--font-family)',
                    outline: 'none'
                  }}
                />
                {categoryErrors.code && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    marginTop: '4px',
                    fontSize: '12px',
                    color: '#dc3545'
                  }}>
                    <AlertCircle size={12} />
                    {categoryErrors.code}
                  </div>
                )}
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'var(--text-primary)',
                  marginBottom: '6px',
                  fontFamily: 'var(--font-family)'
                }}>
                  Label *
                </label>
                <input
                  type="text"
                  value={newCategoryData.label}
                  onChange={(e) => {
                    setNewCategoryData(prev => ({ ...prev, label: e.target.value }));
                    if (categoryErrors.label) {
                      setCategoryErrors(prev => ({ ...prev, label: null }));
                    }
                  }}
                  placeholder="Display name for this item"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: `1px solid ${categoryErrors.label ? '#dc3545' : '#e5e7eb'}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'var(--font-family)',
                    outline: 'none'
                  }}
                />
                {categoryErrors.label && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    marginTop: '4px',
                    fontSize: '12px',
                    color: '#dc3545'
                  }}>
                    <AlertCircle size={12} />
                    {categoryErrors.label}
                  </div>
                )}
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'var(--text-primary)',
                  marginBottom: '6px',
                  fontFamily: 'var(--font-family)'
                }}>
                  Description
                </label>
                <textarea
                  value={newCategoryData.description}
                  onChange={(e) => setNewCategoryData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  placeholder="Optional description"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'var(--font-family)',
                    outline: 'none',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={newCategoryData.isActive}
                  onChange={(e) => setNewCategoryData(prev => ({ ...prev, isActive: e.target.checked }))}
                  style={{ cursor: 'pointer' }}
                />
                <label style={{
                  fontSize: '14px',
                  color: 'var(--text-primary)',
                  fontFamily: 'var(--font-family)',
                  cursor: 'pointer'
                }}>
                  Active (item will appear in dropdowns)
                </label>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              <button
                onClick={handleCreateCategory}
                style={{
                  flex: 1,
                  padding: '10px 16px',
                  backgroundColor: 'var(--primary-color)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  fontFamily: 'var(--font-family)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <Plus size={16} />
                Create Category
              </button>
              <button
                onClick={() => {
                  setShowCreateCategoryModal(false);
                  setNewCategoryData({
                    category: '',
                    code: '',
                    label: '',
                    description: '',
                    isActive: true
                  });
                  setCategoryErrors({});
                }}
                style={{
                  padding: '10px 16px',
                  backgroundColor: 'transparent',
                  color: 'var(--text-secondary)',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  fontFamily: 'var(--font-family)'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default MasterDataManagement;
